import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type L from 'leaflet';
import {
  getLeaflet,
  injectLeafletStyles,
  configureDefaultIcons,
  applyBasemapFilter,
} from '../map/leafletHelpers';
import { createBasemapLayers } from '../map/basemaps';
import { useTheme } from '../../theme';

export interface LocationPickerMapProps {
  centroid: { latitude: number; longitude: number };
  onChange: (centroid: { latitude: number; longitude: number }) => void;
  height?: number;
}

/**
 * Web: Leaflet mini-map with a draggable marker for fine-tuning a
 * structure's centroid. Tapping anywhere on the map moves the pin too.
 */
export function LocationPickerMap({ centroid, onChange, height = 220 }: LocationPickerMapProps) {
  const { isDark } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    injectLeafletStyles();
    configureDefaultIcons();
    if (!containerRef.current || mapRef.current) return;
    const L = getLeaflet();
    const map = L.map(containerRef.current, {
      center: [centroid.latitude, centroid.longitude],
      zoom: 17,
      scrollWheelZoom: true,
    });
    // Same tile source as the main map's standard style, so the two can't
    // drift onto different providers.
    tileLayerRef.current = createBasemapLayers(L, 'standard').base.addTo(map);
    // Same basemap treatment as the main map. Without it this mini-map was
    // a full-color rectangle inside an otherwise monochrome dark dialog.
    applyBasemapFilter(tileLayerRef.current, isDark);
    const marker = L.marker([centroid.latitude, centroid.longitude], {
      draggable: true,
    }).addTo(map);
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      onChangeRef.current({ latitude: pos.lat, longitude: pos.lng });
    });
    map.on('click', (e) => {
      marker.setLatLng(e.latlng);
      onChangeRef.current({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    });
    mapRef.current = map;
    markerRef.current = marker;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      tileLayerRef.current = null;
    };
    // Initial mount only; subsequent centroid updates handled by the next effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply on theme flip (the sheet can stay open across a toggle).
  useEffect(() => {
    applyBasemapFilter(tileLayerRef.current, isDark);
  }, [isDark]);

  // Sync external centroid changes (e.g., a fresh geocode result).
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const current = marker.getLatLng();
    if (
      Math.abs(current.lat - centroid.latitude) < 1e-9 &&
      Math.abs(current.lng - centroid.longitude) < 1e-9
    ) {
      return;
    }
    marker.setLatLng([centroid.latitude, centroid.longitude]);
    map.setView([centroid.latitude, centroid.longitude], map.getZoom());
  }, [centroid.latitude, centroid.longitude]);

  return (
    <View style={[styles.container, { height }]}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { borderRadius: 12, overflow: 'hidden' },
});
